import { isLang, lang } from '@umu/ukit-lang';
import { getMicroPageList, IMicroItemInfo } from '@umu/umu-api';
import { Button, Table } from '@umu/umu-design';
import { MICRO_PAGE_TEMPLATE, MICRO_PAGE_MODE } from '@umu/umu-types';
import React, { useEffect, useState } from 'react';
import { microPageTemplateMapInfo } from '../../dataFlow';
import { customFormatDateTime, unescapeHtml, openWindow } from '@umu/umu-util';
import { onPreviewMicroPageDialog } from './microDetailPreviewPage';
import { If } from 'common/src/components';
import { PaginationV2 } from '../../../pagination';

interface IBasicInfoProps extends React.HTMLAttributes<HTMLDivElement> {
    header?: string | JSX.Element;
    isValid?: boolean;
    selectId?: string;
    onSelected: (id: string) => void;
}
const PAGE_SIZE = 20;

const defaultEmptyTemplate: IMicroItemInfo = {
    access_permission: '',
    create_ts: '',
    creator_id: '',
    desc: '',
    enterprise_id: '',
    id: '',
    is_deleted: '',
    profile_url: '',
    share_url: '',
    title: lang('空模板'),
    update_ts: '',
    updator_id: '',
    user_name: '',
    mode: MICRO_PAGE_TEMPLATE.__INIT__,
    access_close_tip: '',
};
interface ITemplateDataInfo {
    list: IMicroItemInfo[];
    page_info: IPageInfo;
}

const getOperateWidth = () => {
    if (isLang('cn') || isLang('tw')) {
        return 100;
    }
    return 180;
};
const MicroTemplateList = (props: IBasicInfoProps) => {
    const { className = '', selectId = '', header, isValid = false, onSelected, ...rest } = props;
    const [dataInfo, setDataInfo] = useState<ITemplateDataInfo>({
        list: [defaultEmptyTemplate],
        page_info: {
            current_page: 1,
            total_page_num: 1,
            list_total_num: 0,
            size: PAGE_SIZE,
        },
    });
    const [configInfo, setConfigInfo] = useState({
        isLoading: false,
    });
    const onPageChange = (page = 1) => {
        if (configInfo.isLoading) {
            return;
        }
        setConfigInfo({
            isLoading: true,
        });
        getMicroPageList({
            page_mode: MICRO_PAGE_MODE.TEMPLATE,
            page,
            size: PAGE_SIZE,
        }).then((res) => {
            if (res?.page_info?.current_page === page) {
                const isFirstPage = page === 1;
                const list = res?.list || [];
                setDataInfo({
                    list: isFirstPage ? [defaultEmptyTemplate, ...list] : list,
                    page_info: res?.page_info,
                });
            }
            setConfigInfo({
                isLoading: false,
            });
        });
    };
    useEffect(() => {
        onPageChange();
    }, []);

    const columns = [
        {
            title: lang('名称'),
            dataIndex: 'title',
            key: 'title',
            width: 160,
            fixed: 'left',
            ellipsis: true,
            render(value: string, rowData: IMicroItemInfo) {
                const { share_url = '' } = rowData;
                const isLink = share_url && share_url !== '';
                return isLink ? (
                    <Button
                        type="text"
                        className="micro-template-rows-1"
                        isOnlyChildren
                        onClick={() => openWindow(share_url)}
                    >
                        {value}
                    </Button>
                ) : (
                    value
                );
            },
        },
        {
            title: lang('类型'),
            dataIndex: 'mode',
            key: 'mode',
            width: 120,
            render(_value: MICRO_PAGE_TEMPLATE, rowData: IMicroItemInfo) {
                const { id = '', mode = MICRO_PAGE_TEMPLATE.CUSTOM_TEMPLATE } = rowData;
                return id !== '' ? microPageTemplateMapInfo[mode].title : '-';
            },
        },
        {
            title: lang('创建者'),
            dataIndex: 'user_name',
            key: 'user_name',
            width: 160,
            className: 'micro-template-rows-1',
            render(value: string, rowData: IMicroItemInfo) {
                const { profile_url = '' } = rowData;
                return value ? (
                    <Button
                        type="text"
                        className="micro-template-rows-1"
                        isOnlyChildren
                        onClick={() => profile_url && openWindow(profile_url)}
                    >
                        {unescapeHtml(value)}
                    </Button>
                ) : (
                    '-'
                );
            },
        },
        {
            title: lang('创建时间'),
            dataIndex: 'create_ts',
            key: 'create_ts',
            width: 160,
            render(value: number) {
                return value ? customFormatDateTime(Number(value) * 1000, 'yyyy-MM-dd hh:mm') : '-';
            },
        },
        {
            title: lang('操作_actions'),
            dataIndex: 'id',
            key: 'id',
            width: getOperateWidth(),
            fixed: 'right',
            render(value: string, rowData: IMicroItemInfo) {
                const { share_url } = rowData;
                const isSelect = selectId === value;
                const isShare = share_url && share_url !== '';
                return (
                    <div className={`operate-zone operate-zone-${isShare ? '' : 'right'}`}>
                        {isShare && (
                            <Button
                                type="text"
                                className="operate-btn"
                                isOnlyChildren
                                onClick={() =>
                                    onPreviewMicroPageDialog({
                                        shareUrl: share_url,
                                        pageName: rowData.title,
                                    })
                                }
                            >
                                {lang('预览')}
                            </Button>
                        )}

                        <Button
                            type="text"
                            className={`operate-btn operate-btn-${isSelect ? 'active' : ''}`}
                            isOnlyChildren
                            onClick={() => onSelected(value)}
                        >
                            {isSelect ? lang('已选择') : lang('选择')}
                        </Button>
                    </div>
                );
            },
        },
    ];
    return (
        <div className={`micro-template-list-info ${className}`} {...rest}>
            {header && <div className="micro-template-list-info-header">{header}</div>}
            <div className="micro-template-list-info-content">
                <Table
                    useFixedHeader
                    className="micro-template-table-zone"
                    data={dataInfo.list}
                    scroll={{ x: 860, y: 240 }}
                    columns={columns}
                />
            </div>
            <If condition={dataInfo.page_info.total_page_num > 1}>
                <div className="micro-template-pagination">
                    <div className="pagination-total">
                        {lang('共 {$0{0= 条,1= 条}}', dataInfo.page_info.list_total_num)}
                    </div>
                    <PaginationV2
                        currentPage={dataInfo.page_info.current_page}
                        maxPage={dataInfo.page_info.total_page_num}
                        showSizeChanger
                        forceShow={true}
                        onPageChange={onPageChange}
                    />
                </div>
            </If>
        </div>
    );
};

export { MicroTemplateList };
